/**
 * « Preview: what does this member see? » — the missing piece of the Access screen.
 *
 * Today the only way to know what a member sees is to log in as them: the owner
 * discovers the holes by accident (real case 2026-10-08 — Seb, a `tech` member,
 * held `content.rider` but the sidebar hid the rider config section from him).
 *
 * This module answers the question WITHOUT switching accounts, and it does so with
 * the SAME predicates the sidebar itself uses (`canSeeRiderConfig`,
 * `canEditLayoutFor`, `canAnyFinance`, `can`) — so the preview cannot drift from
 * the real navigation. Corollary, stated in the UI: this is a VISIBILITY preview.
 * The write right is enforced separately (page `writeGate` + the server) and a
 * visible link never implies a writable page.
 */
import { can, canAnyFinance, type MemberRoleInput } from './permissions';
import { canEditLayoutsFor } from './layoutAccess';
// La source unique : structure de la navigation + règles de visibilité, lues par la
// sidebar ET par cet aperçu (2026-10-08). Un seul endroit à corriger.
import { PAGE_GROUPS, pageVisibility } from './pageAccess';

/** One sidebar entry: does this member see it? */
export interface PreviewItem {
  /** Stable id, reused by the tests and the React key. */
  id: string;
  /** i18n key of the label — the SIDEBAR's own key, never a copy. */
  labelKey: string;
  visible: boolean;
}

/** A sidebar section (its header + its entries). */
export interface PreviewSection {
  id: string;
  labelKey: string;
  /** Visible when at least one of its entries is. */
  visible: boolean;
  items: PreviewItem[];
}

export interface MemberPreviewInput extends MemberRoleInput {
  /** True when the band link itself exists (the member row is in the band). */
  linked?: boolean;
}

/**
 * Compute the sidebar this member would see on the band.
 *
 * The member is NEVER a superadmin (a superadmin is not delegated per band): the
 * preview only models the delegated surfaces, and `owner` is handled explicitly
 * because an owner holds everything implicitly.
 */
export function previewSidebarFor(input: MemberPreviewInput): PreviewSection[] {
  // ⚠️ L'aperçu ne DÉCRIT plus la navigation : il la LIT. Structure (ordre, libellés,
  // regroupement) et règles viennent tous de `pageAccess.ts` — la même source que la
  // sidebar. Avant, cet aperçu recopiait la structure ET les prédicats : deux copies
  // qui pouvaient diverger, ce qui est précisément la façon dont le cas Seb est resté
  // invisible (l'aperçu aurait dit « visible » d'une page que la nav cachait).
  // ⚠️ `MemberPreviewInput` porte `role` (nom du payload de membre) là où la source
  // unique attend `bandRole` : mapper explicitement. L'aperçu ne modélise JAMAIS un
  // superadmin (« un superadmin ne se délègue pas par band ») — comportement historique
  // conservé.
  const vis = pageVisibility({
    isSuperAdmin: false,
    isShowcase: false,
    bandRole: input.role,
    permissions: input.permissions,
    denied: input.denied,
    linked: input.linked,
  });
  return PAGE_GROUPS.map((group) => {
    const items: PreviewItem[] = group.pages
      // HORS MENU (`hidden`) : même filtre que la sidebar — l'invariant garde == nav == aperçu.
      .filter((page) => !page.hidden)
      .map((page) => ({
      id: page.id,
      labelKey: page.labelKey,
      visible: !!vis[page.id],
    }));
    return {
      id: group.id,
      labelKey: group.labelKey,
      items,
      visible: items.some((i) => i.visible),
    };
  });
}

/**
 * A one-sentence summary of what the member can do, for the owner's glance.
 * Deliberately plain-language: it NAMES the privileges instead of listing ids.
 */
export function describeMember(input: MemberPreviewInput, t: (k: string) => string): string {
  const parts: string[] = [];
  if (input.role === 'owner') return t('access.summaryOwner');
  if (input.role === 'editor' && !input.permissions?.length) return t('access.summaryEditor');
  if (input.role === 'viewer' && !input.permissions?.length) return t('access.summaryViewer');
  if (canAnyFinance(input)) parts.push(t('access.sumFinance'));
  if (canEditLayoutsFor(input)) parts.push(t('access.sumLayouts'));
  for (const p of ['content.rider', 'content.setlists', 'content.songbook', 'content.checklists'] as const) {
    if (can(input, p)) { parts.push(t('access.sumContent')); break; }
  }
  if (can(input, 'team.edit')) parts.push(t('access.sumTeam'));
  if (!parts.length) return t('access.summaryViewer');
  return parts.join(' · ');
}
